import { StorageKey } from 'app/lib/storageKeys';
import type { Identity } from 'app/network/identity';
import { deleteIdentity } from 'app/network/identity-delete';
import doublet from 'doublet';
import { jwtDecode } from 'jwt-decode';
import type { RequestInterceptor } from '../types';
import { storage } from 'app/lib/storage';
import { recordError } from 'app/lib/monitoring';
import { Mutex } from 'async-mutex';
import type { AxiosInstance } from 'axios';
import { isAxiosError } from 'axios';
import { track, Event } from 'app/lib/analytics';
import { getI18nLanguage } from 'app/lib/language';
import * as Device from 'expo-device';
import md5 from 'md5';
import { v4 as uuidv4 } from 'uuid';
import getBrowserFingerprint from 'get-browser-fingerprint';
import { isMobileNative } from 'app/utils/web-helpers';
import { clearSessionState, getLoginAsIdentifier, hydrateSessionState, updateSessionState } from '../session-state';
import { api401DetectedAtom } from 'app/atoms/app';
import { getDefaultStore } from 'jotai';
import { getAuthenticationApiBaseUrl } from '../api-endpoint';

export interface JWTPayload {
  exp: number;
  sub: string;
}

export class UserLoggedOutError extends Error {}

const setAuthorizationHeader = (config: any, accessToken: string) => ({
  ...config,
  headers: {
    ...config.headers,
    Authorization: `Bearer ${accessToken}`,
  },
});

const setAcceptLanguageHeader = (config: any, language: string) => ({
  ...config,
  headers: {
    ...config.headers,
    'Accept-Language': language,
  },
});

const setLoginAsHeader = (config: any, loginAsIdentifier: string) => ({
  ...config,
  headers: {
    ...config.headers,
    'LPC-Login-As': loginAsIdentifier,
  },
});

const deviceDetails = [
  Device.brand,
  Device.designName,
  Device.deviceName,
  Device.deviceType,
  Device.deviceYearClass,
  Device.manufacturer,
  Device.modelId,
  Device.modelName,
  Device.osBuildFingerprint,
  Device.osBuildId,
  Device.osInternalBuildId,
  Device.osName,
  Device.osVersion,
  Device.platformApiLevel,
  Device.productName,
  Device.supportedCpuArchitectures,
  Device.totalMemory,
];
let deviceID: string | null = null;
const newRandomUUID = uuidv4().replaceAll('-', '');

const setDeviceUUIDHeader = async (config: any) => {
  if (!deviceID) {
    let generatedUUID = await storage.readString(StorageKey.GeneratedDeviceUUID);
    if (!generatedUUID) {
      generatedUUID = newRandomUUID;
      await storage.saveString(StorageKey.GeneratedDeviceUUID, generatedUUID);
    }

    deviceID = `D${md5(deviceDetails.join('-'))}-S${generatedUUID}-${!isMobileNative ? `W${(await getBrowserFingerprint()).toFixed(0)}` : 'M'}`;
  }

  return {
    ...config,
    headers: {
      ...config.headers,
      'lpc-device-id': deviceID,
    },
  };
};

const refreshTokenMutex = new Mutex();

export const createSessionManagementInterceptor = (http: AxiosInstance): RequestInterceptor => {
  return async (config) => {
    /**
     * Decodes the current JWT, if any, and either forwards
     * or refreshes it based on its planned expiration
     * date before continuing with the request.
     */

    const language = getI18nLanguage();

    try {
      config = setAcceptLanguageHeader(config, language);

      let session = await hydrateSessionState();
      const { accessToken, currentUserId } = session;

      // Authentication endpoints either establish/refresh/revoke the real account session.
      // Never attach login-as state to them; doing so could route token refresh or
      // revocation to the impersonated user's region.
      const requestPath = config.url?.split('?')[0]?.toLowerCase();
      if (requestPath === '/authentication' || requestPath?.startsWith('/authentication/')) return config;

      config = await setDeviceUUIDHeader(config);

      // We should ignore the interceptor logic when there is no accessToken (and currentUserId) saved.
      if (!accessToken && !currentUserId) return config;

      const isAccessTokenValid = (token?: string | null) => {
        if (!token) return false;
        const [, payload] = doublet(() => jwtDecode<JWTPayload>(token));
        return (payload?.exp ?? 0) > Date.now() / 1000 + 5 * 60;
      };

      if (isAccessTokenValid(accessToken)) {
        const loginAsIdentifier = getLoginAsIdentifier(session);
        if (loginAsIdentifier) {
          config = setLoginAsHeader(config, loginAsIdentifier);
        }

        return setAuthorizationHeader(config, accessToken!);
      }

      // Access token expired (or doesn't exist); need to get a new one (using the refresh token)

      if (refreshTokenMutex.isLocked()) {
        // Another thread is already refreshing. Awaiting new value...
        await refreshTokenMutex.waitForUnlock();
        session = await hydrateSessionState();
        const newAccessToken = session.accessToken;
        if (isAccessTokenValid(newAccessToken)) {
          const loginAsIdentifier = getLoginAsIdentifier(session);
          if (loginAsIdentifier) {
            config = setLoginAsHeader(config, loginAsIdentifier);
          }
          return setAuthorizationHeader(config, newAccessToken!);
        }
      }

      // Requesting new access token from server

      const releaseMutex = await refreshTokenMutex.acquire();

      try {
        session = await hydrateSessionState();
        const latestAccessToken = session.accessToken;
        if (isAccessTokenValid(latestAccessToken)) {
          const loginAsIdentifier = getLoginAsIdentifier(session);
          if (loginAsIdentifier) {
            config = setLoginAsHeader(config, loginAsIdentifier);
          }
          return setAuthorizationHeader(config, latestAccessToken!);
        }

        const refreshToken = session.refreshToken;

        if (!refreshToken) {
          const error = new UserLoggedOutError(`${StorageKey.RefreshToken} missing from local storage.`);

          await Promise.allSettled([
            storage.clear(StorageKey.AccessToken),
            storage.clear(StorageKey.RefreshToken),
            storage.clear(StorageKey.CurrentUserId),
            storage.clear(StorageKey.LoginAsEmail),
            storage.clear(StorageKey.LoginAsUserId),
          ]);
          clearSessionState();

          await deleteIdentity(http);

          track(Event.UnexpectedLogout, { source: 'no_refresh_token', userId: currentUserId ?? 'undefined' });

          throw error;
        }

        const refreshSession = (): Promise<Identity | null> =>
          http
            .put<{ AuthenticationResult: Identity }>(
              '/authentication',
              {
                refreshToken,
                userId: session.currentUserId,
              },
              {
                timeout: 10_000,
                baseURL: getAuthenticationApiBaseUrl(accessToken),
                lpcApiEndpoint: {
                  allowFallback: false,
                  attemptedBaseUrls: [],
                  originalData: { refreshToken, userId: session.currentUserId },
                  preservePreferredEndpoint: true,
                },
              },
            )
            .then((res) => res.data.AuthenticationResult)
            .catch((error: unknown) => {
              recordError({ message: 'Refresh session error:', error });
              throw error;
            });

        const [error, refreshResult] = await doublet(() => refreshSession());

        if (refreshResult) {
          await storage.saveString(StorageKey.AccessToken, refreshResult.AccessToken);
          if (refreshResult.RefreshToken) {
            await storage.saveString(StorageKey.RefreshToken, refreshResult.RefreshToken);
          }
          session = updateSessionState({
            accessToken: refreshResult.AccessToken,
            refreshToken: refreshResult.RefreshToken ?? refreshToken,
          });
          const loginAsIdentifier = getLoginAsIdentifier(session);
          if (loginAsIdentifier) {
            config = setLoginAsHeader(config, loginAsIdentifier);
          }
          return setAuthorizationHeader(config, refreshResult.AccessToken);
        }

        if (isAxiosError(error) && error.response && error.response.status >= 400 && error.response.status < 500) {
          const errorMessage = 'Refreshing access token failed with status code 4XX; logging out the user.';

          try {
            await deleteIdentity(
              http,
              { refreshToken, accessToken: latestAccessToken },
              { skipPushUnregistration: true },
            );
          } catch (revocationError) {
            recordError(revocationError);
          } finally {
            await Promise.allSettled([
              storage.clear(StorageKey.AccessToken),
              storage.clear(StorageKey.RefreshToken),
              storage.clear(StorageKey.CurrentUserId),
              storage.clear(StorageKey.LoginAsEmail),
              storage.clear(StorageKey.LoginAsUserId),
            ]);
            clearSessionState();
          }

          track(Event.UnexpectedLogout, { source: 'token_refresh_4xx', userId: currentUserId ?? 'undefined' });

          throw new UserLoggedOutError(errorMessage);
        }

        if (error instanceof Error) {
          throw error; // rethrow the error to be handled by the caller
        }
      } finally {
        releaseMutex();
      }
    } catch (error) {
      // Handle any errors that occur during session management
      // UserLoggedOutError is expected behaviour (session expired / no refresh token); do not report to Sentry
      if (error instanceof UserLoggedOutError) {
        // Clearing storage alone leaves the app's authenticated state and cached
        // screens active. Notify the root logout watcher for every request,
        // including callers that don't use useQueryLPC.
        getDefaultStore().set(api401DetectedAtom, true);
      } else {
        recordError(error);
      }
      throw error;
    }
  };
};
